import { Button, Chip, Input } from '@emailforge/ui';
import { useState } from 'react';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import {
  type DnsStatus,
  type DnsStatusResponse,
  useCheckSenderDnsMutation,
  useCreateSenderMutation,
  useDeleteSenderMutation,
  useListSendersQuery,
} from '@/features/senders/sendersApi';
import { t } from '@/lib/i18n';

export function SendersScreen() {
  const { data: senders, isLoading } = useListSendersQuery();
  const [createSender, { isLoading: isCreating }] = useCreateSenderMutation();
  const [checkDns, { data: dnsCheck, isLoading: isChecking }] = useCheckSenderDnsMutation();
  const [deleteSender] = useDeleteSenderMutation();

  const [showForm, setShowForm] = useState(false);
  const [fromAddress, setFromAddress] = useState('');
  const [fromName, setFromName] = useState('');
  const [selectedSenderId, setSelectedSenderId] = useState<string | null>(null);

  const submit = async () => {
    await createSender({ fromAddress, fromName: fromName || undefined }).unwrap();
    setShowForm(false);
    setFromAddress('');
    setFromName('');
  };

  const check = async (id: string) => {
    setSelectedSenderId(id);
    await checkDns(id).unwrap();
  };

  return (
    <>
      <TopBar
        crumbs={[t('crumbs.configure'), t('nav.senders')]}
        right={
          !showForm ? (
            <Button variant="flame" size="sm" onClick={() => setShowForm(true)}>
              <Icon.plus size={13} /> Add sender
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          )
        }
      />

      <div style={{ flex: 1, padding: 24, overflowY: 'auto', background: 'var(--paper-2)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          {showForm && (
            <div className="mf-card" style={{ padding: 24, marginBottom: 20, maxWidth: 560 }}>
              <h2 className="display" style={{ fontSize: 20, margin: '0 0 6px' }}>
                Add a sender identity
              </h2>
              <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 16 }}>
                You need to prove control of the domain via SPF + DKIM + DMARC. We check live via Cloudflare DNS.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Field label="From address">
                  <Input
                    
                    value={fromAddress}
                    onChange={(e) => setFromAddress(e.target.value)}
                    placeholder="hello@yourdomain.com" />
                </Field>
                <Field label="From name">
                  <Input
                    
                    value={fromName}
                    onChange={(e) => setFromName(e.target.value)}
                    placeholder="Acme Team" />
                </Field>
                <Button variant="flame" onClick={submit} disabled={!fromAddress || isCreating}>
                  {isCreating ? 'Adding…' : 'Add sender'}
                </Button>
              </div>
            </div>
          )}

          {isLoading && (
            <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              Loading senders…
            </div>
          )}

          {!isLoading && (senders ?? []).length === 0 && !showForm && (
            <div className="mf-card" style={{ padding: 40, textAlign: 'center' }}>
              <div
                className="mono"
                style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.08, color: 'var(--ink-3)' }}
              >
                No senders yet
              </div>
              <h2 className="display" style={{ fontSize: 22, margin: '10px 0 8px' }}>
                Verify your first sending domain
              </h2>
              <p style={{ fontSize: 13.5, color: 'var(--ink-2)', maxWidth: 440, margin: '0 auto 20px' }}>
                SPF · DKIM · DMARC checked live via DNS-over-HTTPS.
              </p>
              <Button variant="flame" onClick={() => setShowForm(true)}>
                <Icon.plus size={13} /> Add sender
              </Button>
            </div>
          )}

          {(senders ?? []).length > 0 && (
            <div style={{ display: 'grid', gap: 12 }}>
              {senders?.map((s) => (
                <div key={s.id} className="mf-card" style={{ padding: 18 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 550 }}>
                        {s.fromName ? `${s.fromName} <${s.fromAddress}>` : s.fromAddress}
                      </div>
                      <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 2 }}>
                        created {new Date(s.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <StatusPill label="SPF" status={s.dnsSpfStatus} />
                    <StatusPill label="DKIM" status={s.dnsDkimStatus} />
                    <StatusPill label="DMARC" status={s.dnsDmarcStatus} />
                    <Button size="sm" onClick={() => check(s.id)}
                      disabled={isChecking && selectedSenderId === s.id}
                    >
                      {isChecking && selectedSenderId === s.id ? 'Checking…' : 'Re-check'}
                    </Button>
                    <Button variant="ghost" size="sm" icon onClick={async () => {
                        if (window.confirm(`Remove ${s.fromAddress}?`)) {
                          await deleteSender(s.id).unwrap();
                        }
                      }}
                    >
                      <Icon.trash size={12} />
                    </Button>
                  </div>

                  {selectedSenderId === s.id && dnsCheck && <DnsWizard result={dnsCheck} />}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function StatusPill({ label, status }: { label: string; status: DnsStatus }) {
  const variant: 'live' | 'err' | 'draft' =
    status === 'verified' ? 'live' : status === 'invalid' ? 'err' : 'draft';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Chip variant={variant}>{status}</Chip>
      <span
        className="mono"
        style={{ fontSize: 9.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.06 }}
      >
        {label}
      </span>
    </div>
  );
}

function DnsWizard({ result }: { result: DnsStatusResponse }) {
  return (
    <div
      style={{
        borderTop: '1px solid var(--line-soft)',
        paddingTop: 16,
        marginTop: 12,
        background: 'var(--paper-2)',
        margin: '12px -18px -18px',
        padding: 18,
      }}
    >
      <div
        className="mono"
        style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.06, color: 'var(--ink-3)', marginBottom: 10 }}
      >
        DNS records · add these at your registrar
      </div>
      <table className="mf-table" style={{ background: 'var(--paper)' }}>
        <thead>
          <tr>
            <th>Type</th>
            <th>Host</th>
            <th>Value</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {result.instructions.map((inst, i) => {
            const key = (['spf', 'dkim', 'dmarc'] as const)[i];
            if (!key) return null;
            const record = result.records[key];
            return (
              <tr key={inst.host}>
                <td className="mono" style={{ fontSize: 11.5 }}>
                  {inst.type}
                </td>
                <td className="mono" style={{ fontSize: 11.5 }}>
                  {inst.host}
                </td>
                <td className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', wordBreak: 'break-all' }}>
                  {inst.value}
                </td>
                <td>
                  <Chip variant={record.status === 'verified' ? 'live' : record.status === 'invalid' ? 'err' : 'draft'}>
                    {record.status}
                  </Chip>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {result.sender.verified && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            background: 'var(--spark-soft)',
            border: '1px solid var(--spark-line)',
            color: 'oklch(0.40 0.14 148)',
            borderRadius: 'var(--r-2)',
            fontSize: 12.5,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Icon.check size={14} />
          Fully verified — you can now send from <b>{result.sender.fromAddress}</b>.
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label
        className="mono"
        style={{
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          color: 'var(--ink-3)',
          display: 'block',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}
